import { functions,db,auth } from './firebase'; 
import { ImagePart } from '../types';
import { addWatermarkToImage } from '../utils/imageHelpers';

// Re-export for backward compatibility
export { fileToBase64 } from '../utils/imageHelpers';

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// --- 1. ROBUST RETRY LOGIC (Kept from LIVE code) ---
// Handles network flukes or server cold starts
async function callWithRetry<T>(
    fn: () => Promise<T>, 
    onStatusUpdate?: (msg: string) => void,
    maxRetries = 3 
): Promise<T> {
    let attempt = 0;
    while (attempt < maxRetries) {
        try {
            return await fn();
        } catch (error: any) {
            // Check for specific backend errors or network timeouts
            const isRetryable = error?.code === 'deadline-exceeded' || 
                                error?.code === 'unavailable' ||
                                error?.message?.includes('network') ||
                                error?.details?.retry === true;
            
            if (isRetryable) {
                attempt++;
                const waitTime = 2000 * attempt; 
                if (onStatusUpdate) {
                    onStatusUpdate(`Server warming up (Attempt ${attempt}/${maxRetries})...`);
                }
                await delay(waitTime);
                continue; 
            }
            throw error;
        }
    }
    throw new Error("Secure server is currently busy. Please try again in a moment.");
}

// --- 2. PROMPT LOGIC (Kept LIVE version for better Indian context) ---

const getPlacementInstruction = (category: string, gender: string): string => {
    const cat = category.toLowerCase();
    
    // Explicit exclusions to prevent model hallucinations
    if (cat.includes('earring') || cat.includes('jhumka') || cat.includes('ear')) {
        return "PLACEMENT: The item MUST be worn on the EAR lobes. Framing: Close-up Portrait (Head & Shoulders). CRITICAL: Do NOT show hands in the frame to avoid confusion with rings. Hair should be styled back to clearly show the ears.";
    }
    if (cat.includes('necklace') || cat.includes('choker') || cat.includes('haar')) {
        return "PLACEMENT: Worn around the NECK. Framing: Portrait (Chest Up). The jewelry should rest naturally on the skin or fabric.";
    }
    if (cat.includes('pendant')) {
        return "PLACEMENT: Worn on a chain around the NECK. Framing: Portrait. Focus on the chest area.";
    }
    if (cat.includes('ring') && !cat.includes('nose')) {
        return "PLACEMENT: Worn on a FINGER. Framing: Macro shot of the Hand. Do not show the face. Focus entirely on the hand.";
    }
    if (cat.includes('couple rings')) {
        return "PLACEMENT: Worn on FINGERS of two hands (Male and Female hands holding). Framing: Close-up of Hands.";
    }
    if (cat.includes('bangle') || cat.includes('bracelet') || cat.includes('kada')) {
        return "PLACEMENT: Worn on the WRIST. Framing: Arm/Hand focus. Avoid full body shots.";
    }
    // Special Indian Items (Kept from Live Code)
    if (cat.includes('vaddanam') || cat.includes('hip chain') || cat.includes('waist')) {
        return "PLACEMENT: Worn around the WAIST/HIPS over a Saree. Framing: Mid-shot (Torso focus).";
    }
    if (cat.includes('maang tikka') || cat.includes('papidi billa')) {
        return "PLACEMENT: Worn on the FOREHEAD/Hair Parting. Framing: Extreme Close-up Headshot.";
    }
    if (cat.includes('nose') || cat.includes('nath') || cat.includes('mukku pudaka')) {
        return "PLACEMENT: Worn on the NOSE (Nostril). Framing: Extreme Close-up Face profile.";
    }
    if (cat.includes('brooch') || cat.includes('button')) {
        return "PLACEMENT: Pinned to the CHEST/GARMENT (Sherwani/Suit). Framing: Upper Body.";
    }
    
    return `PLACEMENT: Worn naturally on the body appropriate for a ${category}.`;
};

const getAttireInstruction = (category: string, gender: string): string => {
    if (gender === 'studio') return "Background: Neutral professional studio backdrop (Grey/Beige/Black). No attire.";

    const cat = category.toLowerCase();
    const indianSpecific = ['vaddanam', 'hip chain', 'maang tikka', 'nose', 'nath', 'choker', 'haar', 'kundan', 'temple', 'jhumka', 'bangle', 'kada', 'sherwani', 'kalgi'];
    
    let attireStyle = "";

    if (indianSpecific.some(k => cat.includes(k))) {
        attireStyle = gender === 'male' 
            ? "Attire: Rich Indian Traditional Sherwani or Kurta. High-end fabric (Silk/Brocade)."
            : "Attire: Rich Indian Traditional Saree or Lehenga. Blouse must be modest, high-neck or boat-neck (or V-neck), properly covering the body. Elegant and Regal.";
    } else {
        // General items (Rings, Pendants, Modern Necklaces)
        attireStyle = gender === 'male'
            ? "Attire: Formal Suit or High-end Smart Casual. Decent and professional."
            : "Attire: Elegant Fusion Wear or High-end Evening Gown. MUST be decent and properly covered (no deep necklines). Fabric should look rich (Silk, Velvet, Satin).";
    }

    return `${attireStyle} The clothing must be decent, traditional/classy, and not distract from the jewelry.`;
};

// --- 3. MAIN SERVICE FUNCTIONS (Merged Features) ---

export const analyzeImageStyle = async (
    image: ImagePart,
    onStatusUpdate?: (msg: string) => void
): Promise<string> => {
    if (onStatusUpdate) onStatusUpdate("Analyzing visual features...");
    
    try {
        const analyzeFn = functions.httpsCallable('analyzeImage');
        const response = await callWithRetry(async () => {
             const res = await analyzeFn({
                image: image.inlineData.data,
                mimeType: image.inlineData.mimeType
            });
            return res.data as any;
        }, onStatusUpdate);
        
        return response.text || "High-end jewelry design with intricate details.";
    } catch (error) {
        console.warn("Analysis failed, using fallback", error);
        return "High-end jewelry design with intricate details, professional lighting, and realistic metallic textures.";
    }
};

export const generateImageFromPrompt = async (
    detailedPrompt: string, 
    quality: 'standard' | 'hd' = 'hd', 
    referenceImages: ImagePart[] = [],
    allowText: boolean = false,
    onStatusUpdate?: (msg: string) => void,
    toolId: string = 'design' // MERGED: Added toolId parameter
): Promise<string> => {
    
    if (onStatusUpdate) onStatusUpdate("Contacting secure server...");

    try {
        const generateDesignFn = functions.httpsCallable('generateDesign');
        
        const payload = {
            prompt: detailedPrompt,
            quality: quality,
            referenceImage: referenceImages.length > 0 ? referenceImages[0].inlineData.data : null,
            type: 'design',
            tool: toolId // MERGED: Pass tool metadata to backend
        };

        // MERGED: Wrapped in callWithRetry
        const response = await callWithRetry(async () => {
            const result = await generateDesignFn(payload);
            return result.data as any;
        }, onStatusUpdate);

        if (response.success && response.image) {
             const base64Data = `data:image/png;base64,${response.image}`;
             return await addWatermarkToImage(base64Data);
        } else {
            throw new Error(response.error || "Generation failed on server.");
        }

    } catch (error: any) {
        console.error("Backend Generation Error:", error);
        if (error.message.includes("Insufficient")) {
             throw new Error("Insufficient Swarnas. Please top up your wallet.");
        }
        throw new Error(error.message || "Server refused generation.");
    }
};

export const generateJewelryMockup = async (
    image: ImagePart,
    category: string,
    gender: 'female' | 'male' | 'studio' = 'female',
    studioBackground?: string,
    quality: 'standard' | 'hd' = 'standard',
    onStatusUpdate?: (msg: string) => void
): Promise<string> => {
    
    if (onStatusUpdate) onStatusUpdate("Processing mockup on server...");

    try {
        const generateMockupFn = functions.httpsCallable('generateDesign');
        
        const placementInstruction = gender === 'studio' 
            ? `Place the ${category} on a professional jewelry display stand/bust appropriate for this specific item type. Do not use a human model.` 
            : getPlacementInstruction(category, gender);

        const attireInstruction = getAttireInstruction(category, gender);

        let aesthetics = "";
        if (quality === 'hd') {
            aesthetics = `
            QUALITY: HIGH-DEFINITION EDITORIAL.
            - Lighting: Cinematic Chiaroscuro or Soft Box Studio Lighting.
            - Details: Micro-details of skin texture (pores, vellus hair) and metal grain must be visible.
            - Resolution: 8K, highly sharp focus on the jewelry.
            - Depth: Realistic depth of field (Bokeh) blurring the background.
            `;
        } else {
            aesthetics = `
            QUALITY: STANDARD E-COMMERCE.
            - Lighting: Flat, bright, even lighting.
            - Details: Clean and smooth skin texture.
            - Focus: Sharp product focus.
            `;
        }

        const prompt = `
        Task: Create a professional jewelry photography mockup.
        Item Category: ${category}
        Model Type: ${gender === 'studio' ? 'Studio Display' : (gender + ' model')}
        Background: ${studioBackground || 'Neutral high-end studio background'}
        
        MANDATORY RULES:
        1. ${placementInstruction}
        2. ${attireInstruction}
        3. The jewelry from the reference image must be the MAIN SUBJECT and integrated realistically.
        
        ${aesthetics}
        `;

        const payload = {
            prompt: prompt,
            quality: quality,
            referenceImage: image.inlineData.data,
            type: 'mockup',
            tool: 'mockup_generator' // MERGED: Explicitly set tool for Mockup
        };

        // MERGED: Wrapped in callWithRetry
        const response = await callWithRetry(async () => {
            const result = await generateMockupFn(payload);
            return result.data as any;
        }, onStatusUpdate);

        if (response.success && response.image) {
             const base64Data = `data:image/png;base64,${response.image}`;
             return await addWatermarkToImage(base64Data);
        } else {
            throw new Error(response.error || "Mockup generation failed.");
        }
    } catch (error: any) {
         console.error("Backend Mockup Error:", error);
         if (error.message.includes("Insufficient")) {
             throw new Error("Insufficient Swarnas. Please top up.");
        }
         throw new Error(error.message || "Server error.");
    }
};